import { Table, Select } from '@/components/ui'
import { router } from '@inertiajs/react'
import { useState } from 'react'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import type { Attendee, Pagination, RegistrationField } from '../types'
import { useAttendeeColumns, buildQrLink } from '../hooks/useAttendeeColumns'
import QRPreviewModal from '@/components/QRPreviewModal'

interface AttendeeTableProps {
  attendees: Attendee[]
  pagination: Pagination
  eventSlug: string
  registrationFields: RegistrationField[]
  visibleColumns: Record<string, boolean>
  columnOrder: string[]
  selectedRowKeys: React.Key[]
  onSelectionChange: (selectedRowKeys: React.Key[]) => void
  onViewProfile: (id: number) => void
  onEditAttendee: (id: number) => void
  onDeleteAttendee: (id: number, name: string) => void
  onPrintBadge?: (attendee: Attendee) => void
  isPrintEnabled?: boolean
  printDisabledReason?: string
  printingAttendeeIds?: Set<number>
  onMarkAttendance?: (attendee: Attendee) => void
  markAttendanceDisabledReason?: string
  showDeleteAction?: boolean
}

export default function AttendeeTable({
  attendees,
  pagination,
  eventSlug,
  registrationFields,
  visibleColumns,
  columnOrder,
  selectedRowKeys,
  onSelectionChange,
  onViewProfile,
  onEditAttendee,
  onDeleteAttendee,
  onPrintBadge,
  isPrintEnabled,
  printDisabledReason,
  printingAttendeeIds,
  onMarkAttendance,
  markAttendanceDisabledReason,
  showDeleteAction = true,
}: AttendeeTableProps) {
  const [goToPage, setGoToPage] = useState('')
  const [qrRecord, setQrRecord] = useState<Attendee | null>(null)

  const allColumns = useAttendeeColumns({
    registrationFields,
    onViewProfile,
    onEditAttendee,
    onDeleteAttendee,
    onShowQr: setQrRecord,
    onPrintBadge,
    isPrintEnabled,
    printDisabledReason,
    printingAttendeeIds,
    onMarkAttendance,
    markAttendanceDisabledReason,
    showDeleteAction,
  })

  const columnsByKey = Object.fromEntries(allColumns.map((c) => [c.key as string, c]))

  // Build ordered columns respecting visibility (actions always last/right)
  const orderedKeys = [
    ...columnOrder,
    ...Object.keys(columnsByKey).filter((k) => !columnOrder.includes(k) && k !== 'actions'),
  ]
  const columns = orderedKeys
    .map((key) => columnsByKey[key])
    .filter((col) => col)
    .filter((col) => (col.key === 'actions' ? true : visibleColumns[col.key as string]))

  // ensure actions column stays at end
  const actionsCol = columnsByKey['actions']
  if (actionsCol && !columns.includes(actionsCol)) {
    columns.push(actionsCol)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectionChange,
  }

  const totalPages = Math.ceil(pagination.total_count / pagination.per_page)

  const handlePageChange = (page: number) => {
    router.get(`/events/${eventSlug}/attendees`, { page, per_page: pagination.per_page }, {
      preserveState: true,
    })
  }

  const handlePageSizeChange = (size: number) => {
    router.get(`/events/${eventSlug}/attendees`, { page: 1, per_page: size }, {
      preserveState: true,
    })
  }

  const handleGoToPage = (value: string) => {
    const pageNum = parseInt(value, 10)
    if (pageNum >= 1 && pageNum <= totalPages) {
      handlePageChange(pageNum)
    }
    setGoToPage('')
  }

  return (
    <div>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={attendees}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        size="middle"
        showSorterTooltip={false}
        className="ava-data-table"
        pagination={false}
      />

      {/* Custom Pagination */}
      <div className="ava-pagination mt-4">
        <span className="ava-pagination-total">Total {pagination.total_count} items</span>

        <button
          className="ava-pagination-prev"
          disabled={pagination.current_page <= 1}
          onClick={() => handlePageChange(pagination.current_page - 1)}
        >
          <LeftOutlined />
        </button>

        <span className="ava-pagination-current">{pagination.current_page} / {totalPages}</span>

        <button
          className="ava-pagination-next"
          disabled={pagination.current_page >= totalPages}
          onClick={() => handlePageChange(pagination.current_page + 1)}
        >
          <RightOutlined />
        </button>

        <Select
          value={pagination.per_page}
          onChange={handlePageSizeChange}
          className="ava-pagination-size-changer"
          size="small"
          options={[
            { value: 25, label: '25 / page' },
            { value: 50, label: '50 / page' },
            { value: 100, label: '100 / page' },
          ]}
        />

        <span className="ava-pagination-go-to-label">Go to</span>
        <input
          type="text"
          className="ava-pagination-go-to-input"
          value={goToPage}
          onChange={(e) => setGoToPage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleGoToPage((e.target as HTMLInputElement).value)
            }
          }}
          onBlur={(e) => {
            if (e.target.value) {
              handleGoToPage(e.target.value)
            }
          }}
        />
      </div>

      <QRPreviewModal
        open={!!qrRecord}
        onClose={() => setQrRecord(null)}
        title={qrRecord?.full_name ?? 'QR Code'}
        url={qrRecord?.qr_code ? buildQrLink(qrRecord.qr_code) : ''}
        value={qrRecord?.qr_code}
        subtitle={qrRecord?.qr_code}
        downloadFilename={qrRecord ? `${qrRecord.full_name}-qr` : 'qr-code'}
      />
    </div>
  )
}
